import { IconGift, IconX } from "@tabler/icons-react"
import { type FC, useState } from "react"
import styled from "styled-components"

import Button from "~/components/Button"
import InputField from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { cardColor } from "~/utilities/styles"
import { query } from "~/utilities/trpc"

const FreeTrial: FC = () => {
	const user = useUser()
	const platform = usePlatform()!
	const toast = useToast()

	const [isDismissOpen, setDismissOpen] = useState(false)
	const [trialDismissReason, setTrialDismissReason] = useState("")

	const startTrial = query.payment.creator.startAddonTrial.useMutation({
		async onSuccess() {
			await user.reload()
			toast.showSuccess("Your free 7-day Scale addon gift is active! It ends automatically, with no charge.")
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to start trial! Please try again later.")
		},
	})
	const dismissTrial = query.payment.creator.dismissAddonTrial.useMutation({
		async onSuccess() {
			await user.reload()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to dismiss trial! Please try again later.")
		},
	})

	const lifetimePlan = platform.plan.type === "appsumo" ? "AppSumo lifetime plan" : "lifetime plan"

	return (
		<Container>
			<DefaultModal
				isOpen={isDismissOpen}
				onClose={startTrial.isPending || dismissTrial.isPending ? undefined : () => setDismissOpen(false)}
			>
				<ModalTitle>Prefer to skip your free gift?</ModalTitle>
				<ModalText>
					Your {lifetimePlan} and its included features stay yours. As a thank-you for supporting Forento,
					we're gifting you 7 days of the optional Scale addon. No credit card, no automatic subscription, and
					nothing to cancel.
				</ModalText>
				<ModalText>
					After 7 days, you continue on your lifetime plan. Features only available through the addon require
					a paid subscription to keep using them. You can review the features and prices before activating. If
					you'd rather focus on your current plan, it's fine to skip the gift.
				</ModalText>
				<Button variant="link" onClick={routes.settings.billing.plans()}>
					View addon features and prices
				</Button>
				<ModalInputs>
					<InputField
						label="What made you decide to skip the gift?"
						value={trialDismissReason}
						onChange={setTrialDismissReason}
					/>
				</ModalInputs>
				<ModalButtons>
					<Button
						variant="secondary"
						onClick={() => setDismissOpen(false)}
						isDisabled={startTrial.isPending || dismissTrial.isPending}
					>
						Go back
					</Button>
					<Button
						variant="secondary"
						onClick={() => startTrial.mutate()}
						isDisabled={dismissTrial.isPending}
						isLoading={startTrial.isPending}
					>
						Start my free 7 days
					</Button>
					<Button
						variant="primary-danger"
						onClick={() => dismissTrial.mutate(trialDismissReason)}
						isDisabled={startTrial.isPending || trialDismissReason.trim().length === 0}
						isLoading={dismissTrial.isPending}
					>
						Skip gift
					</Button>
				</ModalButtons>
			</DefaultModal>
			<Icon />
			<Title>A thank-you gift: 7 days of the Scale addon, free</Title>
			<Description>
				Your {lifetimePlan} stays yours. Enjoy the optional Scale addon on us for 7 days.
				<br />
				No credit card. No automatic charges. The gift ends automatically and you keep your lifetime plan.
				<br />
				<Button variant="link" onClick={routes.settings.billing.plans()}>
					View addon features and prices
				</Button>
			</Description>
			<ClaimButton variant="primary" onClick={() => startTrial.mutate()} isLoading={startTrial.isPending}>
				Start my free 7 days
			</ClaimButton>
			<DismissButton onClick={() => setDismissOpen(true)}>
				<IconX />
			</DismissButton>
		</Container>
	)
}

const Container = styled.div`
	background-color: ${cardColor.purple};
	border-radius: 24px;
	padding: 24px;
	display: grid;
	grid-template-areas:
		"icon title claim dismiss"
		"icon description claim dismiss";
	grid-template-columns: max-content 1fr max-content max-content;
	grid-template-rows: max-content max-content;
	align-items: center;
	gap: 4px 16px;

	@media (max-width: 760px) {
		grid-template-areas:
			"icon dismiss"
			"title title"
			"description description"
			"claim claim";
		grid-template-columns: 1fr max-content;
		padding: 16px;
		gap: 12px;
	}
`

const Icon = styled(IconGift)`
	grid-area: icon;
`

const Title = styled.div`
	grid-area: title;
	font-size: 20px;
	font-weight: 600;
	line-height: 1;

	@media (max-width: 760px) {
		line-height: 1.2;
	}
`

const Description = styled.div`
	grid-area: description;

	@media (max-width: 760px) {
		br {
			display: none;
		}
	}
`

const ClaimButton = styled(Button)`
	grid-area: claim;

	@media (max-width: 760px) {
		width: 100%;
	}
`

const DismissButton = styled(Button)`
	grid-area: dismiss;
`

export default FreeTrial
